import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Stack, DataGrid, Typography, IconButton, Icon, Tooltip } from '@emburse/embark-core';
import { EyeOpenIcon, EyeCloseIcon } from '@emburse/embark-icons';
import { $t, useTranslation } from '../../../hooks/useTranslation';
import useAccountConfig from '../../../hooks/useAccountConfig';
import { formatDate } from '../../../util/dateAdapter';
function DisplayAccountUpdates({ changesFound, proposed, current }) {
    return (_jsxs(Stack, { children: [changesFound && _jsx(Typography, { children: proposed }), _jsx(Typography, { sx: {
                    textDecoration: changesFound && 'line-through'
                }, children: current })] }));
}
function NOCMaskUnmask({ cell, allowUnmask, onUnmask, tooltip }) {
    return (_jsxs("div", { children: [allowUnmask && (_jsx(IconButton, { color: "secondary", size: "medium", disabled: !allowUnmask, onClick: () => onUnmask(cell), children: _jsx(Tooltip, { title: tooltip, children: _jsx(Icon, { iconUrl: cell?.row?.masked ? EyeOpenIcon : EyeCloseIcon }) }) })), !allowUnmask && (_jsx(Tooltip, { title: tooltip, children: _jsx(IconButton, { color: "secondary", size: "medium", disabled: !allowUnmask, onClick: () => onUnmask(cell), children: _jsx(Icon, { iconUrl: cell?.row?.masked ? EyeOpenIcon : EyeCloseIcon }) }) }))] }));
}
function NOCTable({ rows, loading, onUnmask }) {
    useTranslation();
    const { accountConfig } = useAccountConfig();
    const detectChanges = (val) => val?.length > 0 || false;
    const tableRows = rows.map((row) => ({
        ...row,
        id: row.PK,
        accountNumber: {
            current: row.UnMaskedData?.CurrentAccountNum || row.Data.CurrentAccountNum,
            proposed: row.UnMaskedData?.ProposedAccountNum || row.Data.ProposedAccountNum,
            changesFound: detectChanges(row.Data.ProposedAccountNum)
        },
        routingNumber: {
            current: row.UnMaskedData?.CurrentRoutingCode || row.Data.CurrentRoutingCode,
            proposed: row.UnMaskedData?.ProposedRoutingCode || row.Data.ProposedRoutingCode,
            changesFound: detectChanges(row.Data.ProposedRoutingCode)
        },
        accountType: {
            current: row.UnMaskedData?.CurrentAccountType || row.Data.CurrentAccountType,
            proposed: row.UnMaskedData?.ProposedAccountType || row.Data.ProposedAccountType,
            changesFound: detectChanges(row.Data.ProposedAccountType)
        },
        masked: !row.UnMaskedData
    }));
    const allowUnmask = (row) => {
        const { WriteEmployeeDirectDepositAccount, WriteInvoiceVendorDirectDepositAccount } = accountConfig.UIFlags;
        return row.IsInvoiceVendor
            ? WriteInvoiceVendorDirectDepositAccount
            : WriteEmployeeDirectDepositAccount;
    };
    const getUnmaskTooltipText = (row) => {
        if (allowUnmask(row)) {
            return row.masked ? $t('Show unmasked account data') : $t('Hide account data');
        }
        return $t('You do not have permission to view sensitive banking information for this account');
    };
    const columns = [
        {
            id: 'dateCol',
            field: 'CreateDate',
            headerName: $t('Date'),
            width: 150,
            sortable: !loading,
            renderCell: (cell) => _jsx(Typography, { children: formatDate(cell.value) })
        },
        {
            id: 'beneCol',
            field: 'BeneficiaryName',
            headerName: $t('Beneficiary'),
            minWidth: 150,
            flex: 2,
            sortable: !loading,
            renderCell: (cell) => _jsx(Typography, { children: cell.value })
        },
        {
            id: 'accountNumCol',
            field: 'accountNumber',
            headerName: $t('Account Number'),
            width: 200,
            sortable: false,
            renderCell: (cell) => (_jsx(Stack, { alignItems: "flex-start", children: _jsx(DisplayAccountUpdates, { changesFound: cell.formattedValue.changesFound, proposed: cell.formattedValue.proposed, current: cell.formattedValue.current }) }))
        },
        {
            id: 'routingNumCol',
            field: 'routingNumber',
            headerName: $t('Routing Number'),
            width: 200,
            sortable: !loading,
            renderCell: (cell) => (_jsx(Stack, { alignItems: "flex-end", children: _jsx(DisplayAccountUpdates, { changesFound: cell.formattedValue.changesFound, proposed: cell.formattedValue.proposed, current: cell.formattedValue.current }) }))
        },
        {
            id: 'bankNameCol',
            field: 'BankName',
            headerName: $t('Bank Name'),
            flex: 2,
            sortable: !loading,
            renderCell: (cell) => _jsx(Typography, { children: cell.value })
        },
        {
            id: 'accountTypeCol',
            field: 'accountType',
            headerName: $t('Account Type'),
            width: 130,
            flex: 1,
            sortable: !loading,
            renderCell: (cell) => (_jsx(Stack, { alignItems: "flex-start", children: _jsx(DisplayAccountUpdates, { changesFound: cell.formattedValue.changesFound, proposed: cell.formattedValue.proposed, current: cell.formattedValue.current }) }))
        },
        {
            id: 'unmask',
            field: 'isUnMaskedData',
            headerName: '',
            width: 60,
            sortable: false,
            renderCell: (cell) => (_jsx(NOCMaskUnmask, { allowUnmask: allowUnmask(cell.row), onUnmask: () => onUnmask(cell), cell: cell, tooltip: getUnmaskTooltipText(cell.row) }))
        }
    ];
    return (_jsx(DataGrid, { autoHeight: true, loading: loading, pageSizeOptions: [10, 50, 100], rows: tableRows, columns: columns, sortModel: [
            {
                field: 'createDate',
                sort: 'desc'
            }
        ], localeText: {
            noRowsLabel: $t('No automated account updates found at this time.')
        } }));
}
export default NOCTable;
